import { KEYBOARD_KEYS } from '@treyd-io/core/constants/keyboardKeys';
import { useEvent } from '@treyd-io/core/hooks/useEvent';
import { isEmpty, join, split } from 'lodash';
import React, {
  ChangeEvent,
  FocusEvent,
  KeyboardEvent,
  RefObject,
  createRef,
  forwardRef,
  useEffect,
  useRef,
} from 'react';

import type {
  StackProps as MuiStackProps,
  TextFieldProps as MuiTextFieldProps,
} from '@mui/material';
import { Stack, TextField } from '@mui/material';
import outlinedInputClasses from '@mui/material/OutlinedInput/outlinedInputClasses';

type TextFieldProps = Omit<
  MuiTextFieldProps,
  'onChange' | 'select' | 'multiline' | 'defaultValue' | 'value' | 'autoFocus'
>;

type StackProps = Omit<MuiStackProps, 'onChange' | 'onBlur'>;

interface BaseMuiOtpFieldProps {
  value?: string;
  length?: number;
  autoFocus?: boolean;
  TextFieldsProps?: TextFieldProps;
  onComplete?: (value: string) => void;
  onChange?: (value: string) => void;
  onBlur?: (value: string, isCompleted: boolean) => void;
}

type MuiOtpFieldProps = StackProps & BaseMuiOtpFieldProps;

type ValueSplitted = {
  character: string;
  inputRef: RefObject<HTMLInputElement>;
}[];

const getFilledArray = <
  T extends (v: number, k: number) => unknown,
  Y extends ReturnType<T>,
>(
  range: number,
  mapfn: T
): Y[] => {
  return range <= 0 ? [] : (Array.from({ length: range }, mapfn) as Y[]);
};

const updateIndex = <T extends unknown[]>(
  array: T,
  indexItem: number,
  item: T[keyof T]
): T => {
  return array.map((chipItem, index) => {
    return indexItem === index ? item : chipItem;
  }) as T;
};

const mergeArrayStringFromIndex = (
  array: readonly string[],
  arrayToMerge: readonly string[],
  fromIndex: number
): string[] => {
  return array.reduce(
    (accumulator, currentValue, index) => {
      const { characters, restArrayMerged } = accumulator;

      if (index < fromIndex) {
        return {
          restArrayMerged,
          characters: [...characters, currentValue],
        };
      }

      const [firstCharacter, ...restArrayWithoutFirstCharacter] =
        restArrayMerged;

      return {
        restArrayMerged: restArrayWithoutFirstCharacter,
        characters: [...characters, firstCharacter || ''],
      };
    },
    {
      restArrayMerged: arrayToMerge,
      characters: [] as string[],
    }
  ).characters;
};

export const OtpField = forwardRef(
  (props: MuiOtpFieldProps, propRef: MuiOtpFieldProps['ref']) => {
    const {
      value = '',
      length = 6,
      autoFocus,
      onChange,
      TextFieldsProps,
      onComplete,
      className,
      onBlur,
      ...restStackProps
    } = props;

    const initialValue = useRef(value);
    const onCallbackEvent = useEvent(onComplete);

    const matchIsCompletedEvent = useEvent((filledStrings: string) => {
      return {
        isCompleted: filledStrings.length >= length,
        finalValue: filledStrings.slice(0, length),
      };
    });

    const {
      onPaste,
      onFocus,
      onKeyDown,
      className: TextFieldClassName,
      onBlur: TextFieldOnBlur,
      inputProps: TextFieldNativeInputProps,
      InputProps: TextFieldInputProps,
      ...restTextFieldsProps
    } = TextFieldsProps || {};

    useEffect(() => {
      const { isCompleted, finalValue } = matchIsCompletedEvent(
        initialValue.current
      );

      if (isCompleted) {
        onCallbackEvent(finalValue);
      }
    }, [length, onCallbackEvent, matchIsCompletedEvent]);

    const valueSplitted: ValueSplitted = getFilledArray(
      length as number,
      (_, index: number) => {
        return {
          character: (value as string)[index] || '',
          inputRef: createRef<HTMLInputElement>(),
        };
      }
    );

    const getIndexByInputElement = (inputElement: HTMLInputElement) => {
      return valueSplitted.findIndex(({ inputRef }) => {
        return inputRef.current === inputElement;
      });
    };

    const getCharactersSplitted = () => {
      return valueSplitted.map(({ character }) => {
        return character;
      });
    };

    const replaceCharOfValue = (charIndex: number, charValue: string) => {
      const newValueSplitted = updateIndex(
        getCharactersSplitted(),
        charIndex,
        charValue
      );

      return join(newValueSplitted, '');
    };

    const focusInputByIndex = (inputIndex: number) => {
      valueSplitted[inputIndex]?.inputRef.current?.focus();
    };

    const selectInputByIndex = (inputIndex: number) => {
      valueSplitted[inputIndex]?.inputRef.current?.select();
    };

    const manageCaretForNextInput = (currentInputIndex: number) => {
      if (currentInputIndex + 1 === length) {
        return;
      }

      if (valueSplitted[currentInputIndex + 1].character) {
        selectInputByIndex(currentInputIndex + 1);
      } else {
        focusInputByIndex(currentInputIndex + 1);
      }
    };

    const matchIsCharIsValid = (character: string, _index: number) => {
      return /^\d+$/.test(character);
    };

    const handleOneInputChange = (event: ChangeEvent<HTMLInputElement>) => {
      const initialChar = event.target.value[0] || '';
      let character = initialChar;
      const currentInputIndex = getIndexByInputElement(event.target);

      // handle backspace so check character
      if (character && !matchIsCharIsValid(character, currentInputIndex)) {
        character = '';
      }

      const newValue = replaceCharOfValue(currentInputIndex, character);

      onChange?.(newValue);

      const { isCompleted, finalValue } = matchIsCompletedEvent(newValue);

      if (isCompleted) {
        onComplete?.(finalValue);
      }

      // Char is valid so go to next input
      if (character !== '') {
        // handle when the filled input is before the input selected
        if (newValue.length - 1 < currentInputIndex) {
          selectInputByIndex(newValue.length);
        } else {
          manageCaretForNextInput(currentInputIndex);
        }

        // Only for backspace so don't go to previous input if the char is invalid
      } else if (initialChar === '') {
        if (newValue.length <= currentInputIndex) {
          selectInputByIndex(currentInputIndex - 1);
        }
      }
    };

    const handleOneInputFocus = (event: FocusEvent<HTMLInputElement>) => {
      event.preventDefault();
      event.target.select();
      onFocus?.(event);
    };

    const handleOneInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      const inputElement = event.target as HTMLInputElement;
      const startPos = inputElement.selectionStart;
      const endPos = inputElement.selectionEnd;
      const currentInputIndex = getIndexByInputElement(inputElement);
      const isCaretBeforeChar = startPos === 0 && endPos === 0;

      if (inputElement.value === event.key) {
        event.preventDefault();
        manageCaretForNextInput(currentInputIndex);
      } else if (KEYBOARD_KEYS.BACKSPACE === event.key) {
        if (!inputElement.value) {
          event.preventDefault();

          selectInputByIndex(currentInputIndex - 1);
          // Caret is before the character and there is a character, so remove it
        } else if (isCaretBeforeChar) {
          event.preventDefault();

          const newValue = replaceCharOfValue(currentInputIndex, '');
          onChange?.(newValue);

          if (newValue.length <= currentInputIndex) {
            selectInputByIndex(currentInputIndex - 1);
          }
        }
      } else if (KEYBOARD_KEYS.ARROW_LEFT === event.key) {
        event.preventDefault();
        selectInputByIndex(currentInputIndex - 1);
      } else if (KEYBOARD_KEYS.ARROW_RIGHT === event.key) {
        event.preventDefault();
        selectInputByIndex(currentInputIndex + 1);
      } else if (KEYBOARD_KEYS.HOME === event.key) {
        event.preventDefault();
        selectInputByIndex(0);
      } else if (KEYBOARD_KEYS.END === event.key) {
        event.preventDefault();
        selectInputByIndex(valueSplitted.length - 1);
      }

      onKeyDown?.(event);
    };

    const handleOneInputPaste = (
      event: React.ClipboardEvent<HTMLInputElement>
    ) => {
      event.preventDefault();
      const content = event.clipboardData.getData('text/plain');
      const inputElement = event.target as HTMLInputElement;
      // Apply from where an input is empty or equal to the input selected
      const currentInputIndex = valueSplitted.findIndex(
        ({ character, inputRef }) => {
          return character === '' || inputRef.current === inputElement;
        }
      );
      const currentCharacter = getCharactersSplitted();

      const characters = mergeArrayStringFromIndex(
        currentCharacter,
        split(content),
        currentInputIndex
      ).map((character: string, index: number) => {
        return matchIsCharIsValid(character, index) ? character : '';
      });
      const newValue = join(characters, '');
      onChange?.(newValue);

      const { isCompleted, finalValue } = matchIsCompletedEvent(newValue);

      if (isCompleted) {
        onComplete?.(finalValue);
        selectInputByIndex(length - 1);
      } else {
        selectInputByIndex(newValue.length);
      }

      onPaste?.(event);
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      TextFieldOnBlur?.(event);
      const anInputIsFocused = valueSplitted.some(({ inputRef }) => {
        return inputRef.current === event.relatedTarget;
      });

      if (!anInputIsFocused) {
        const { isCompleted, finalValue } = matchIsCompletedEvent(value);
        onBlur?.(finalValue, isCompleted);
      }
    };

    return (
      <Stack
        direction="row"
        gap={2}
        alignItems="center"
        ref={propRef}
        className={`MuiOtpField-Box ${className || ''}`}
        {...restStackProps}>
        {valueSplitted.map(({ character, inputRef }, index) => {
          return (
            <TextField
              autoFocus={autoFocus ? index === 0 : false}
              autoComplete="one-time-code"
              value={character}
              inputRef={inputRef}
              className={`MuiOtpField-TextField MuiOtpField-TextField-${
                index + 1
              } ${TextFieldClassName || ''}`}
              onPaste={handleOneInputPaste}
              onFocus={handleOneInputFocus}
              onChange={handleOneInputChange}
              onKeyDown={handleOneInputKeyDown}
              onBlur={handleBlur}
              key={index}
              color="info"
              InputProps={{
                sx: ({ spacing }) => {
                  const hasValue = !isEmpty(character);
                  if (hasValue) {
                    return {
                      fontSize: { xs: spacing(3), sm: spacing(6) },
                      color: 'info.main',
                      backgroundColor: 'info.50',
                      padding: { xs: 1, sm: 1.5 },
                      [`& .${outlinedInputClasses.notchedOutline}`]: {
                        display: 'none',
                      },
                    };
                  }
                  return {
                    fontSize: { xs: spacing(3), sm: spacing(6) },
                    padding: { xs: 1, sm: 1.5 },
                  };
                },
                ...TextFieldInputProps,
              }}
              inputProps={{
                placeholder: '0',
                onFocus: (event) => {
                  event.target.placeholder = '';
                },
                onBlur: (event) => {
                  event.target.placeholder = '0';
                },
                style: {
                  textAlign: 'center',
                },
                ...TextFieldNativeInputProps,
              }}
              {...restTextFieldsProps}
            />
          );
        })}
      </Stack>
    );
  }
);

OtpField.displayName = 'OtpField';
